import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { useCreatePriceChangeBatch, useDryRunPriceChangeBatch, usePriceChangeBatches, useCustomerGroups, usePriceLists, useSalesChannels } from '../../hooks';
import { formatDateTime } from '../../lib/format';
import type { ToastSeverity } from '../../lib/useToast';
import { batchStatusColor, BATCH_STATUS_LABELS } from '../../model/options';
import type { CreatePriceChangeBatchRequest, PriceChangeBatchResponse } from '../../model/priceBatchTypes';
import { PriceChangeBatchDetailView } from './PriceChangeBatchDetailView';
import { PriceChangeBatchWizardDialog } from './PriceChangeBatchWizardDialog';

export interface PriceChangeBatchesPanelProps {
  /** `catalog.pricing.manage` — create a batch. */
  canManage: boolean;
  /** `catalog.pricing.bulk` — dry-run/apply (mass-change actions), required IN ADDITION TO `canManage`. */
  canBulk: boolean;
  /** Pre-targets a new batch at this price list — the list-detail page's "Reprice into list" entry point. */
  initialTargetListCode?: string;
  /** Opens the dry-run/apply detail for this batch on mount — the card drawer's "Source" chip deep
   * link (design spec §8.5/§12.4, `?tab=bulk&view=batches&batch=`). */
  initialBatchId?: string;
  onResult: (severity: ToastSeverity, message: string) => void;
}

function adjustmentLabel(batch: PriceChangeBatchResponse): string {
  if (batch.adjustmentType === 'PERCENT') return `${batch.adjustmentValue > 0 ? '+' : ''}${batch.adjustmentValue}%`;
  if (batch.adjustmentType === 'ABSOLUTE') return `${batch.adjustmentValue > 0 ? '+' : ''}${batch.adjustmentValue} ₽`;
  return `= ${batch.adjustmentValue} ₽`;
}

/**
 * The "Repricing" sub-view (design spec §8.8) — bulk repricing batches: list, create wizard, and
 * the dry-run diff / apply detail (rendered inline; batches have no routed sub-page, unlike price
 * lists). **Contract gap (disclosed):** no cancel endpoint exists on the live BFF contract (see
 * `catalogPriceBatchesApi.ts`'s header) — the design mock's "cancel" row action is not rendered.
 */
export function PriceChangeBatchesPanel({
  canManage,
  canBulk,
  initialTargetListCode,
  initialBatchId,
  onResult,
}: PriceChangeBatchesPanelProps) {
  const { t } = useTranslation('catalog');
  // Auto-opens, pre-targeted, when arriving from the list-detail "Reprice into list" entry point
  // (design spec §8.3) — landing on a closed wizard would defeat that button's whole point.
  const [wizardOpen, setWizardOpen] = useState(Boolean(initialTargetListCode));
  // Opens straight into the dry-run/apply detail when arriving from the card drawer's batch deep
  // link (design spec §12.4) — mount-time seed only, the same class as `initialTargetListCode`
  // above: `PricingPage` echoes `?batch=` back while this tab stays active, but a later prop
  // change (without an intervening unmount) is not tracked. Gated on `canBulk`, same as the list
  // query below — every path on this tag 403s without it, so a shared deep link never fires a
  // request the viewer has no right to see.
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(
    canBulk ? (initialBatchId ?? null) : null,
  );

  // The shipped BFF gates every path on this tag — including the plain GET list — behind
  // `catalog.pricing.bulk` (`access.requirePricingBulk()` on every method of
  // `CatalogPricingControllerImpl`'s batches block, GETs included). Disabling the query when the
  // caller lacks it avoids firing a request that can only ever 403, and lets the render below show
  // a clean "you don't have this permission" state instead of a generic load-failure ErrorState.
  const batchesQuery = usePriceChangeBatches({ page: 0, size: 50 }, { enabled: canBulk });
  const createMutation = useCreatePriceChangeBatch();
  const dryRunMutation = useDryRunPriceChangeBatch();
  const channelsQuery = useSalesChannels();
  const listsQuery = usePriceLists();
  const groupsQuery = useCustomerGroups();

  if (selectedBatchId) {
    return (
      <PriceChangeBatchDetailView
        batchId={selectedBatchId}
        canBulk={canBulk}
        onBack={() => setSelectedBatchId(null)}
        onResult={onResult}
      />
    );
  }

  const canCreate = canManage && canBulk;
  const createButton = (
    <Button variant="contained" startIcon={<AddRoundedIcon />} disabled={!canCreate} onClick={() => setWizardOpen(true)}>
      {t('bulkBatchesPanel.create')}
    </Button>
  );

  const handleCreate = (body: CreatePriceChangeBatchRequest) => {
    createMutation.mutate(body, {
      onSuccess: (created) => {
        dryRunMutation.mutate(created.id, {
          onSuccess: () => {
            setWizardOpen(false);
            setSelectedBatchId(created.id);
            onResult('success', t('bulkBatchesPanel.created'));
          },
          onError: (error) => {
            setWizardOpen(false);
            setSelectedBatchId(created.id);
            onResult('error', error.message);
          },
        });
      },
      onError: (error) => onResult('error', error.message),
    });
  };

  const targetChip = (batch: PriceChangeBatchResponse): string => {
    if (batch.targetPriceListCode) {
      return t('bulkBatchesPanel.target.listChip', {
        name: (listsQuery.data ?? []).find((l) => l.code === batch.targetPriceListCode)?.name ?? batch.targetPriceListCode,
      });
    }
    if (batch.targetCustomerGroupCode) {
      return t('bulkBatchesPanel.target.groupChip', {
        name: (groupsQuery.data ?? []).find((g) => g.code === batch.targetCustomerGroupCode)?.name ?? batch.targetCustomerGroupCode,
      });
    }
    if (batch.targetChannelCode) {
      return (channelsQuery.data ?? []).find((c) => c.code === batch.targetChannelCode)?.name ?? batch.targetChannelCode;
    }
    return t('bulkBatchesPanel.target.base');
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="flex-end" sx={{ mb: 2 }}>
        <Tooltip title={t('bulkBatchesPanel.refreshAria')}>
          <IconButton aria-label={t('bulkBatchesPanel.refreshAria')} onClick={() => void batchesQuery.refetch()}>
            <RefreshRoundedIcon />
          </IconButton>
        </Tooltip>
        {canCreate ? (
          createButton
        ) : (
          <Tooltip title={t('bulkBatchesPanel.noBulkPermission')}>
            <span>{createButton}</span>
          </Tooltip>
        )}
      </Stack>

      {!canBulk ? (
        <EmptyState
          icon={<LockOutlinedIcon sx={{ fontSize: 48 }} />}
          title={t('bulkBatchesPanel.noBulkPermissionTitle')}
          description={t('bulkBatchesPanel.noBulkPermission')}
        />
      ) : batchesQuery.isLoading ? (
        <LoadingState label={t('bulkBatchesPanel.loading')} />
      ) : batchesQuery.isError ? (
        <ErrorState message={t('bulkBatchesPanel.loadError')} onRetry={() => void batchesQuery.refetch()} />
      ) : (batchesQuery.data ?? []).length === 0 ? (
        <EmptyState
          title={t('bulkBatchesPanel.empty.title')}
          description={t('bulkBatchesPanel.empty.description')}
          action={canCreate ? <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setWizardOpen(true)}>{t('bulkBatchesPanel.create')}</Button> : undefined}
        />
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('bulkBatchesPanel.table.name')}</TableCell>
                <TableCell>{t('bulkBatchesPanel.table.change')}</TableCell>
                <TableCell>{t('bulkBatchesPanel.table.target')}</TableCell>
                <TableCell align="right">{t('bulkBatchesPanel.table.products')}</TableCell>
                <TableCell>{t('bulkBatchesPanel.table.status')}</TableCell>
                <TableCell>{t('bulkBatchesPanel.table.updated')}</TableCell>
                <TableCell align="right">{t('bulkBatchesPanel.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(batchesQuery.data ?? []).map((batch) => (
                <TableRow key={batch.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedBatchId(batch.id)}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {batch.name || <Typography component="span" sx={{ fontFamily: '"JetBrains Mono", monospace' }}>{batch.id}</Typography>}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {adjustmentLabel(batch)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={targetChip(batch)} />
                  </TableCell>
                  <TableCell align="right">{batch.itemsTotal}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={batchStatusColor(batch.status)}
                      icon={batch.status === 'APPLYING' ? <CircularProgress size={12} color="inherit" /> : undefined}
                      label={t(BATCH_STATUS_LABELS[batch.status] ?? batch.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDateTime(batch.updatedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title={t('bulkBatchesPanel.openAction')}>
                      <IconButton size="small" aria-label={t('bulkBatchesPanel.openActionAria', { name: batch.name || batch.id })} onClick={() => setSelectedBatchId(batch.id)}>
                        <OpenInNewRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <PriceChangeBatchWizardDialog
        open={wizardOpen}
        initialTargetListCode={initialTargetListCode}
        pending={createMutation.isPending || dryRunMutation.isPending}
        onClose={() => setWizardOpen(false)}
        onSubmit={handleCreate}
      />
    </Box>
  );
}
